import { IS_LOCAL } from "configs/AppConfig"
import DOMPurify from "dompurify"

import { StringTransformer } from "@shared/utils/StringTransformer"

// IT-3469: Helper type for string values coming from "unknown" sources, which
// could be empty, null or undefined
type NullableString = string | undefined | null

// Helper type for string transformations
type StringTransformation = (input: NullableString) => string

const BLOCK_TAGS = ["p", "div", "li", "h1", "h2", "h3", "h4", "h5", "h6", "tr", "td", "th", "blockquote"]

DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (!node) {
    return
  }

  // Security measures for links
  if (node.tagName === "A") {
    let href = node.getAttribute("href")
    if (href) {
      // Replace http with https whenever possible (some outgoing plato links are http which should be https)
      if (!IS_LOCAL && href.startsWith("http:")) {
        href = `https:${href.slice(5)}`
        node.setAttribute("href", href)
      }

      // Allow https protocol only (http is only allowed during development)
      const anchor = document.createElement("a")
      anchor.href = href
      const isValidProtocol = (IS_LOCAL && anchor.protocol === "http:") || anchor.protocol === "https:"
      if (!isValidProtocol) {
        node.removeAttribute("href")
      }
    }

    // Additional security measure for outgoing links
    const target = node.getAttribute("target")
    if (target === "_blank") {
      node.setAttribute("rel", "noopener noreferrer")
    } else if (target !== null) {
      node.removeAttribute("target")
    }
  }
})

export const sanitizeHTML = (input: NullableString, returnDom?: boolean) => {
  if (!input?.length) {
    return ""
  }

  return DOMPurify.sanitize(input, { ADD_ATTR: ["target"], RETURN_DOM: returnDom })
}

const sanitizeHtmlNoTags: StringTransformation = (input) => {
  if (!input?.length) {
    return ""
  }

  return DOMPurify.sanitize(input, { ALLOWED_TAGS: [] })
}

const replaceNbsp: StringTransformation = (input) => {
  if (!input?.length) {
    return ""
  }

  return input.replaceAll("&nbsp;", " ")
}

const addNewlinesAfterBlocks: StringTransformation = (input) => {
  if (!input?.length) {
    return ""
  }

  // add newline after closing tags of block elements
  const closingTagsRegex = new RegExp(`</(${BLOCK_TAGS.join("|")})>`, "gi")

  let out = input.replace(closingTagsRegex, "</$1>\n")

  // normalize <br>, <br/> and <br /> to include a newline
  out = out.replace(/<br\s*\/?>/gi, "<br>\n")

  return out
}

const collapseAndTrimLines: StringTransformation = (input) => {
  if (!input?.length) {
    return ""
  }

  return input
    .replace(/\r\n?/g, "\n") // normalize CRLFs to LF
    .replace(/\n\s*\n+/g, "\n") // collapse multiple newlines
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .join("\n")
    .trim()
}

/**
 * Removes all HTML tags from the given input, returning the plain text content
 * to prevent HTML tags from displaying. Block-level elements are separated with newlines
 * to maintain readability.
 */
export const stripHtmlTags: StringTransformation = (input) => {
  if (!input?.length) {
    return ""
  }

  return new StringTransformer()
    .add(addNewlinesAfterBlocks)
    .add(sanitizeHtmlNoTags)
    .add(replaceNbsp)
    .add(collapseAndTrimLines)
    .transform(input)
}

/** Sanitizes HTML content by removing images but preserving other formatting */
export const sanitizeHtmlWithoutImages: StringTransformation = (input) => {
  if (!input?.length) {
    return ""
  }

  return DOMPurify.sanitize(input, {
    // Subset of HTML tags, with text content, that we allow
    // https://nl.wikipedia.org/wiki/Lijst_van_HTML-elementen
    ALLOWED_TAGS: [
      ...BLOCK_TAGS,
      "a",
      "span",
      "em",
      "strong",
      "b",
      "i",
      "u",
      "ul",
      "ol",
      "br",
      "hr",
      "table",
      "thead",
      "tbody",
      "tr",
      "th",
      "td",
      "code",
      "pre",
    ],
    ADD_ATTR: ["target", "href", "style", "class"],
    RETURN_DOM: false,
  })
}
